"use client";

import { useId, type ReactNode } from "react";

import { ResponsivePickerShell } from "../../_components/responsive-picker-shell";
import { CloseIcon } from "../../ui/i/close";
import { PillButton } from "../../ui/pill-button";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorEl?: HTMLElement | null;
  title: string;
  /** ポップアップ中身（スクロール領域に入る） */
  children: ReactNode;
  /** クリアボタンが有効か（ドラフト選択がある場合） */
  canClear: boolean;
  /** 決定ボタンが有効か（ドラフト選択が確定要件を満たす場合） */
  canConfirm: boolean;
  onClear: () => void;
  onConfirm: () => void;
  variant?: "filter-sidebar" | "filter-search";
};

/**
 * フィルターポップアップ枠組み。
 *
 * Figma（5141-8041 ポップアップ）準拠:
 * - 右上 ×（キャンセル = draft 破棄）
 * - 中身（スクロール可）
 * - フッタ: クリア（丸型ゴーストボタン）＋ 決定（丸型塗りボタン）右寄せ
 *
 * 状態モデル:
 * - × / 外側クリック / Esc → onOpenChange(false) → 呼び出し側が draft 破棄
 * - クリア → onClear()（ドラフトを全解除、まだ未適用）
 * - 決定 → onConfirm()（ドラフトを一括適用）→ 呼び出し側が setOpen(false)
 */
export function FilterPopupShell({
  open,
  onOpenChange,
  anchorEl,
  title,
  children,
  canClear,
  canConfirm,
  onClear,
  onConfirm,
  variant = "filter-sidebar",
}: Props) {
  const titleId = useId();

  return (
    <ResponsivePickerShell
      open={open}
      onOpenChange={onOpenChange}
      anchorEl={anchorEl}
      dimBackdrop={false}
      ariaLabelledBy={titleId}
      variant={variant}
    >
      <div className="flex h-full flex-col">
        <div className="flex shrink-0 items-center justify-end p-[12px]">
          <span id={titleId} className="sr-only">
            {title}
          </span>
          {/* × ボタン: bg-dx-surface-base でポップアップ背景と統一 */}
          <button
            // ドラフトを破棄して閉じるだけで、押されなかったことに情報が無い
            data-analytics-ignore
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="閉じる"
            className="bg-dx-surface-base focus-visible:ring-dx-border-focus flex size-[36px] items-center justify-center rounded-full focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:outline-none"
          >
            <CloseIcon className="size-[24px]" />
          </button>
        </div>

        {/* flex flex-col で子の flex-1 が効くようにする */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-[12px]">
          {children}
        </div>

        {/* フッタ: クリア + 決定
            Figma（5141-7173）: 外側 h-[48px] が押下域、内側 h-[40px] pill が視覚的ボタン。
            - クリア: ghost（border-dx-border-default + text-dx-text-default、背景透明）
            - 決定: 塗り（bg-dx-bg-inverse #454139 + text-dx-text-inverse）
        */}
        <div className="flex shrink-0 items-center justify-end gap-[12px] p-[12px]">
          {/* どのポップアップかは直前の opened_*_filter で分かるので、id は役割ごとに 1 つにする */}
          <PillButton
            data-analytics-id="room_type_browse_clicked_filter_clear"
            variant="ghost"
            size="medium"
            onClick={onClear}
            disabled={!canClear}
          >
            クリア
          </PillButton>
          <PillButton
            data-analytics-id="room_type_browse_searched_filters"
            variant="solid"
            size="medium"
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            決定
          </PillButton>
        </div>
      </div>
    </ResponsivePickerShell>
  );
}
